import * as React from "react";
import { type MotionValue } from "motion/react";

/**
 * Magnify orchestration shared from `<NavRail>` down to every
 * `<RailNavButton>`.
 *
 * **Why a context (the one-listener rule).** The dock-magnify needs each tile
 * to know the cursor's position along the rail's scroll axis. The naive port
 * would give every button its own `onMouseMove` listener; with N buttons that
 * is N listeners all firing on the same pointer move. Instead `<NavRail>` owns
 * ONE `mouseY` `MotionValue` and a SINGLE `onMouseMove` on the nav element,
 * and publishes that value here. Each `RailNavButton` reads it (deriving its
 * own distance-to-cursor with `useTransform`) — Motion values update outside
 * React render, so this stays cheap no matter how many tiles subscribe.
 *
 * `value` is `null` when a button is rendered outside a `<NavRail>` (e.g. in
 * isolation or under reduced motion where the rail disables magnify): the
 * button then holds its resting scale and skips the proximity wiring.
 */
export interface NavRailMagnifyContextValue {
  /**
   * Live cursor coordinate along the rail's scroll axis (vertical → `clientY`).
   * Set to `Infinity` when the cursor is off the rail, which the per-tile
   * distance ramp clamps to "rest". `null` here means magnify is off entirely.
   */
  mouseAxisPos: MotionValue<number> | null;
  /** Whether magnify is enabled for this rail (false under reduced-motion or `magnify={false}`). */
  enabled: boolean;
}

export const NavRailMagnifyContext = React.createContext<NavRailMagnifyContextValue>({
  mouseAxisPos: null,
  enabled: false,
});

/**
 * Read the rail's shared magnify state. Returns the disabled default when
 * called outside a `<NavRail>`, so `<RailNavButton>` composes safely anywhere.
 */
export function useNavRailMagnify(): NavRailMagnifyContextValue {
  return React.useContext(NavRailMagnifyContext);
}
